import { isUndefined } from 'lodash';
import SlaTag from 'components/atoms/tags/SlaTag';
import DoNotCallTag from 'components/atoms/tags/DoNotCallTag';
import EmailUnsubscribedTag from 'components/atoms/tags/EmailUnsubscribedTag';
import CopyableText from 'components/atoms/buttons/copyable-text';
import { SnippetsOutlined } from '@ant-design/icons';
import SalesForceIntegrationTag from 'components/atoms/tags/sales-force-integration-tag';
import { useRef, useState, useEffect } from 'react';

import s from '../styles.module.scss';

import type { DoNotCallRestrictionType } from 'infrastructure/enums';
import type { FC } from 'react';

interface TitleProps {
  fullName: string;
  birthDate: string;
  patientGuid: string;
  sla?: number;
  rpmDoNotCall: boolean;
  ccmDoNotCall: boolean;
  rpmRestrictionType?: DoNotCallRestrictionType | null;
  ccmRestrictionType?: DoNotCallRestrictionType | null;
  isShowEmailTag?: boolean;
  pcmStatus?: boolean;
  showSalesForceIntegration?: boolean;
}

const Title: FC<TitleProps> = ({
  fullName,
  birthDate,
  sla,
  rpmDoNotCall,
  ccmDoNotCall,
  rpmRestrictionType,
  ccmRestrictionType,
  isShowEmailTag,
  pcmStatus: _pcmStatus,
  showSalesForceIntegration,
  patientGuid,
}) => {
  const measureRef = useRef<HTMLDivElement>(null);
  const [tagsWrapped, setTagsWrapped] = useState(false);

  const MAX_NAME_LENGTH = 50;
  const displayName =
    fullName.length > MAX_NAME_LENGTH
      ? `${fullName.slice(0, MAX_NAME_LENGTH)}...`
      : fullName;

  useEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      setTagsWrapped(el.scrollHeight > el.offsetHeight + 2);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const tagsContent = (
    <>
      {rpmDoNotCall && (
        <DoNotCallTag module="RPM" restrictionType={rpmRestrictionType} small />
      )}
      {ccmDoNotCall && (
        <DoNotCallTag module="CCM" restrictionType={ccmRestrictionType} small />
      )}
      {!isUndefined(sla) && <SlaTag hours={sla} small />}
      {isShowEmailTag && <EmailUnsubscribedTag small />}
      {showSalesForceIntegration && <SalesForceIntegrationTag small />}
    </>
  );

  return (
    <div className={s['title-wrapper']}>
      <div ref={measureRef} className={s['name-tags-row-probe']}>
        <h4 className={s.title}>{displayName}</h4>
        <div className={s['tags-wrapper']}>{tagsContent}</div>
      </div>

      <div className={s['name-tags-row']}>
        <h4
          className={s.title}
          data-cy="patient-name"
          title={fullName.length > MAX_NAME_LENGTH ? fullName : undefined}
        >
          {displayName}
        </h4>
        {!tagsWrapped && <div className={s['tags-wrapper']}>{tagsContent}</div>}
      </div>

      <div className={s['patient-info-details']}>
        <span className={s.date} data-cy="patient-birthday-date">
          DOB: {birthDate}
        </span>
        <span className={s['text-separator']}>|</span>
        <div className={s['patient-id']}>
          <span>Pt ID:</span>
          <div data-cy="patient-id" className={s['patient-id-container']}>
            <span className={s['patient-id-value']}>{patientGuid}</span>
            <span className={s['patient-id-copy-icon']}>
              <CopyableText
                value={patientGuid}
                icon={<SnippetsOutlined style={{ fontSize: '14px' }} />}
              />
            </span>
          </div>
        </div>
      </div>

      {tagsWrapped && <div className={s['tags-wrapper']}>{tagsContent}</div>}
    </div>
  );
};
export default Title;
